import type { HTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { LinkProps } from 'react-router-dom'
import { AutoPageInfoBar } from '../../layout/PageInfoContext'
import { cx } from './utils'
import { isOfferStatusKey, offerStatusTone } from '@proffer/shared'

export type AppTone =
  | 'violet'
  | 'emerald'
  | 'amber'
  | 'red'
  | 'zinc'
  | 'sky'
  | 'bronze'
  | 'silver'
  | 'gold'

const toneClass: Record<AppTone, string> = {
  violet:
    'bg-violet-50 text-violet-800 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-900/60',
  emerald:
    'bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900/60',
  amber:
    'bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-900/60',
  red: 'bg-red-50 text-red-800 ring-red-200 dark:bg-red-950/40 dark:text-red-200 dark:ring-red-900/60',
  zinc: 'bg-zinc-100 text-zinc-700 ring-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-zinc-700',
  sky: 'bg-sky-50 text-sky-800 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:ring-sky-900/60',
  bronze:
    'bg-orange-100 text-orange-950 ring-orange-300 dark:bg-orange-950/50 dark:text-orange-100 dark:ring-orange-700/80',
  silver:
    'bg-slate-200 text-slate-800 ring-slate-300 dark:bg-slate-700/90 dark:text-slate-100 dark:ring-slate-500',
  gold:
    'bg-yellow-100 text-yellow-950 ring-yellow-400 dark:bg-yellow-950/45 dark:text-yellow-100 dark:ring-yellow-600/70',
}

const toneSurfaceClass: Record<AppTone, string> = {
  violet:
    'border-violet-200/80 bg-gradient-to-br from-violet-50/90 via-white to-fuchsia-50/50 dark:border-violet-900/45 dark:from-violet-950/30 dark:via-zinc-900/80 dark:to-fuchsia-950/15',
  emerald:
    'border-emerald-200/80 bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/50 dark:border-emerald-900/45 dark:from-emerald-950/30 dark:via-zinc-900/80 dark:to-teal-950/15',
  amber:
    'border-amber-200/80 bg-gradient-to-br from-amber-50/90 via-white to-orange-50/50 dark:border-amber-900/45 dark:from-amber-950/30 dark:via-zinc-900/80 dark:to-orange-950/15',
  red: 'border-red-200/80 bg-gradient-to-br from-red-50/90 via-white to-rose-50/50 dark:border-red-900/45 dark:from-red-950/30 dark:via-zinc-900/80 dark:to-rose-950/15',
  zinc: 'border-zinc-200/90 bg-gradient-to-br from-zinc-100/80 via-white to-slate-100/55 dark:border-zinc-800 dark:from-zinc-900/90 dark:via-zinc-900/75 dark:to-slate-900/45',
  sky: 'border-sky-200/80 bg-gradient-to-br from-sky-50/90 via-white to-cyan-50/50 dark:border-sky-900/45 dark:from-sky-950/30 dark:via-zinc-900/80 dark:to-cyan-950/15',
  bronze:
    'border-orange-300/90 bg-gradient-to-br from-orange-100/90 via-amber-50/90 to-orange-200/60 dark:border-orange-700/55 dark:from-orange-950/35 dark:via-zinc-900/80 dark:to-amber-950/20',
  silver:
    'border-slate-300/90 bg-gradient-to-br from-slate-100/90 via-gray-50/90 to-slate-200/70 dark:border-slate-500/60 dark:from-slate-800/50 dark:via-zinc-900/80 dark:to-slate-900/45',
  gold:
    'border-yellow-400/90 bg-gradient-to-br from-yellow-50/90 via-amber-50/90 to-yellow-200/60 dark:border-yellow-600/50 dark:from-yellow-950/30 dark:via-zinc-900/80 dark:to-amber-950/25',
}

export function AppPage({
  children,
  size = 'default',
  className,
  showPageInfo = true,
}: {
  children: ReactNode
  size?: 'narrow' | 'default' | 'wide'
  className?: string
  /** Αυτόματο info banner από κατάλογο οθονών · false για κρύψιμο */
  showPageInfo?: boolean
}) {
  const maxWidth =
    size === 'narrow'
      ? 'max-w-app-narrow'
      : size === 'wide'
        ? 'max-w-app-wide'
        : 'max-w-app'

  return (
    <div className={cx('mx-auto px-4 py-8 sm:px-6 sm:py-10', maxWidth, className)}>
      {showPageInfo ? <AutoPageInfoBar className="mb-6" /> : null}
      {children}
    </div>
  )
}

export function AppPageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: ReactNode
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0 text-left">
        {eyebrow ? (
          <div className="mb-1 text-eyebrow font-semibold uppercase text-violet-600 dark:text-violet-400">
            {eyebrow}
          </div>
        ) : null}
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-3xl">
          {title}
        </h1>
        {description ? (
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          {actions}
        </div>
      ) : null}
    </div>
  )
}

export function AppCard({
  children,
  className,
  padded = true,
  as: Component = 'div',
  ...props
}: HTMLAttributes<HTMLElement> & {
  children: ReactNode
  className?: string
  padded?: boolean
  as?: 'div' | 'section' | 'article' | 'form'
}) {
  return (
    <Component
      className={cx(
        'rounded-app border border-zinc-200/90 bg-gradient-to-br from-white via-white to-violet-50/35 shadow-app-soft backdrop-blur-sm dark:border-zinc-800 dark:from-zinc-900/85 dark:via-zinc-900/75 dark:to-violet-950/10 dark:shadow-black/20',
        padded && 'p-5 sm:p-6',
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  )
}

export function AppSectionHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
          {title}
        </h2>
        {description ? (
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  )
}

export function AppBadge({
  children,
  tone = 'zinc',
  className,
}: {
  children: ReactNode
  tone?: AppTone
  className?: string
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1',
        toneClass[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

export function AppMetricCard({
  label,
  value,
  detail,
  tone = 'violet',
}: {
  label: string
  value: ReactNode
  detail?: ReactNode
  tone?: AppTone
}) {
  return (
    <AppCard className={toneSurfaceClass[tone]}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            {label}
          </p>
          <p className="mt-3 text-2xl font-bold tabular-nums text-zinc-900 dark:text-white">
            {value}
          </p>
        </div>
        <span className={cx('mt-1 h-3 w-3 rounded-full ring-4', toneClass[tone])} />
      </div>
      {detail ? (
        <p className="mt-3 text-xs leading-5 text-zinc-500 dark:text-zinc-400">
          {detail}
        </p>
      ) : null}
    </AppCard>
  )
}

export function AppStatePanel({
  title,
  children,
  tone = 'zinc',
}: {
  title: string
  children?: ReactNode
  tone?: AppTone
}) {
  return (
    <AppCard className="text-center">
      <AppBadge tone={tone}>{title}</AppBadge>
      {children ? (
        <div className="mt-3 text-sm text-zinc-600 dark:text-zinc-400">
          {children}
        </div>
      ) : null}
    </AppCard>
  )
}

export function AppTableShell({ children }: { children: ReactNode }) {
  return (
    <AppCard padded={false} className="overflow-hidden">
      <div className="overflow-x-auto">{children}</div>
    </AppCard>
  )
}

export function AppToolbar({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <AppCard
      className={cx(
        'mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      {children}
    </AppCard>
  )
}

export function AppEmptyState({
  title,
  description,
  action,
  tone = 'violet',
  className,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  tone?: AppTone
  className?: string
}) {
  return (
    <div
      className={cx(
        'rounded-app-lg border border-dashed bg-gradient-to-b px-6 py-12 text-center shadow-app-soft',
        tone === 'violet'
          ? 'border-violet-200/80 from-violet-50/85 to-white dark:border-violet-500/25 dark:from-violet-950/30 dark:to-zinc-900/40'
          : 'border-zinc-300 from-white/80 to-zinc-50/70 dark:border-zinc-700 dark:from-zinc-900/70 dark:to-zinc-950/50',
        className,
      )}
    >
      <AppBadge tone={tone}>{title}</AppBadge>
      {description ? (
        <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-zinc-600 dark:text-zinc-400">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </div>
  )
}

export function AppListItem({
  children,
  className,
  interactive = false,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  interactive?: boolean
}) {
  return (
    <div
      className={cx(
        'rounded-app border border-zinc-200/90 bg-gradient-to-br from-white via-white to-sky-50/30 p-4 shadow-app-soft backdrop-blur-sm dark:border-zinc-800 dark:from-zinc-900/85 dark:via-zinc-900/75 dark:to-sky-950/10 sm:p-5',
        interactive &&
          'transition hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-brand-soft dark:hover:border-violet-600/50',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

const appListSurfaceClassName =
  'block rounded-app border border-zinc-200/90 bg-gradient-to-br from-white via-white to-violet-50/35 p-4 shadow-app-soft backdrop-blur-sm transition hover:-translate-y-0.5 hover:border-violet-300 hover:from-violet-50/70 hover:to-indigo-50/60 hover:shadow-brand-soft dark:border-zinc-800 dark:from-zinc-900/85 dark:via-zinc-900/75 dark:to-violet-950/10 dark:hover:border-violet-600/50 dark:hover:from-violet-950/25 sm:p-5'

export function AppListSurface({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  children: ReactNode
}) {
  return (
    <div className={cx(appListSurfaceClassName, className)} {...props}>
      {children}
    </div>
  )
}

export function AppListLink({
  children,
  className,
  ...props
}: LinkProps & {
  children: ReactNode
}) {
  return (
    <Link className={cx(appListSurfaceClassName, className)} {...props}>
      {children}
    </Link>
  )
}

export function AppStatusBadge({
  children,
  status,
  tone,
}: {
  children: ReactNode
  status?: string | null
  tone?: AppTone
}) {
  const resolvedTone =
    tone ??
    (status && isOfferStatusKey(status)
      ? (offerStatusTone(status) as AppTone)
      : status === 'ACCEPTED' || status === 'active'
        ? 'emerald'
        : status === 'REJECTED' || status === 'canceled' || status === 'failed'
          ? 'red'
          : status === 'DRAFT' || status === 'pending'
            ? 'amber'
            : status === 'ARCHIVED'
              ? 'zinc'
              : 'violet')

  return <AppBadge tone={resolvedTone}>{children}</AppBadge>
}

export function PageBreadcrumb({
  items,
  className,
}: {
  items: Array<{ label: ReactNode; to?: string }>
  className?: string
}) {
  return (
    <p
      className={cx(
        'mb-1 text-eyebrow font-semibold uppercase text-violet-600 dark:text-violet-400',
        className,
      )}
    >
      {items.map((item, index) => (
        <span key={index}>
          {item.to ? (
            <Link
              to={item.to}
              className="font-semibold text-violet-600 hover:text-violet-500 dark:text-violet-400"
            >
              {item.label}
            </Link>
          ) : (
            item.label
          )}
          {index < items.length - 1 ? (
            <span className="mx-1.5 font-normal text-zinc-400 dark:text-zinc-500">
              /
            </span>
          ) : null}
        </span>
      ))}
    </p>
  )
}

export function MobileActionBar({ children }: { children: ReactNode }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-zinc-200 bg-white/95 p-3 shadow-2xl backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/95 sm:hidden">
      <div className="flex gap-2">{children}</div>
    </div>
  )
}
